import { EventEmitter } from '../events/MapEvent';
import type { Geometry } from '../geometry/MultiGeometry';
import type { Style, StyleFunction } from '../style/Style';
import { Extent } from '../geometry/Extent';

export class Feature extends EventEmitter {
  public id: string | number | undefined;
  public geometry: Geometry | undefined;
  public properties: Record<string, any>;
  private _style?: Style | StyleFunction;

  constructor(options?: {
    geometry?: Geometry;
    properties?: Record<string, any>;
    id?: string | number;
    style?: Style | StyleFunction;
  }) {
    super();
    this.geometry = options?.geometry;
    this.properties = options?.properties || {};
    this.id = options?.id;
    this._style = options?.style;
  }

  getId(): string | number | undefined {
    return this.id;
  }

  setId(id: string | number): void {
    this.id = id;
  }

  getGeometry(): Geometry | undefined {
    return this.geometry;
  }

  setGeometry(geometry: Geometry): void {
    this.geometry = geometry;
    this.emit('change:geometry', { geometry });
    this.emit('change');
  }

  getGeometryType(): string | undefined {
    return this.geometry?.type;
  }

  get(key: string): any {
    return this.properties[key];
  }

  set(key: string, value: any): void {
    const oldValue = this.properties[key];
    this.properties[key] = value;
    this.emit('propertychange', { key, oldValue });
    this.emit('change');
  }

  getProperties(): Record<string, any> {
    return { ...this.properties };
  }

  setProperties(props: Record<string, any>): void {
    this.properties = { ...props };
    this.emit('change');
  }

  unset(key: string): void {
    delete this.properties[key];
    this.emit('change');
  }

  has(key: string): boolean {
    return key in this.properties;
  }

  getKeys(): string[] {
    return Object.keys(this.properties);
  }

  getStyle(): Style | StyleFunction | undefined {
    return this._style;
  }

  setStyle(style: Style | StyleFunction): void {
    this._style = style;
    this.emit('change');
  }

  clone(): Feature {
    return new Feature({
      geometry: this.geometry,
      properties: { ...this.properties },
      id: this.id,
      style: this._style,
    });
  }

  toGeoJSON(): GeoJSON.Feature {
    return {
      type: 'Feature',
      id: this.id as any,
      geometry: this.geometry
        ? ('toGeoJSON' in this.geometry
            ? (this.geometry as any).toGeoJSON()
            : null)
        : null as any,
      properties: { ...this.properties },
    };
  }
}

export class FeatureCollection {
  private features: Feature[];

  constructor(features: Feature[] = []) {
    this.features = [...features];
  }

  getFeatures(): Feature[] {
    return [...this.features];
  }

  getLength(): number {
    return this.features.length;
  }

  getAt(index: number): Feature {
    return this.features[index];
  }

  push(feature: Feature): void {
    this.features.push(feature);
  }

  remove(feature: Feature): Feature | undefined {
    const idx = this.features.indexOf(feature);
    if (idx >= 0) {
      return this.features.splice(idx, 1)[0];
    }
    return undefined;
  }

  clear(): void {
    this.features = [];
  }

  forEach(callback: (feature: Feature, index: number) => void): void {
    this.features.forEach(callback);
  }

  filter(predicate: (feature: Feature) => boolean): Feature[] {
    return this.features.filter(predicate);
  }

  map<T>(callback: (feature: Feature) => T): T[] {
    return this.features.map(callback);
  }

  getExtent(): Extent {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const f of this.features) {
      const geom = f.getGeometry();
      if (geom && 'getExtent' in geom) {
        const ext = (geom as any).getExtent();
        if (ext.minX < minX) minX = ext.minX;
        if (ext.minY < minY) minY = ext.minY;
        if (ext.maxX > maxX) maxX = ext.maxX;
        if (ext.maxY > maxY) maxY = ext.maxY;
      }
    }
    return new Extent(minX, minY, maxX, maxY);
  }

  toGeoJSON(): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: this.features.map((f) => f.toGeoJSON()),
    };
  }
}
